import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface VulnRow {
  readonly vulnerabilityId: string;
  readonly vulnerabilityCode: string;
  readonly title: string;
  readonly severity: string;
  readonly status: string;
  readonly slaBreached: boolean;
}

const SEVERITIES = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
const STATUSES = ['', 'OPEN', 'TRIAGED', 'IN_REMEDIATION', 'REMEDIATED', 'VERIFIED', 'ACCEPTED', 'FALSE_POSITIVE', 'CLOSED'];

/**
 * GR L30.04 — Vulnerability Register (`/security/vulnerabilities`). Tracks the
 * `vulnerability_register` with severity, remediation status and SLA-breach
 * flag. Filterable by severity and status.
 */
export function GrVulnerabilitiesPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<VulnRow[]>([]);
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (severity) params.set('severity', severity);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: VulnRow[] }>(`/v1/security/vulnerabilities${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, severity, status]);

  return (
    <main aria-label="security-vulnerabilities">
      <h1>{t('gr30.vulns.title')}</h1>
      <div className="filters">
        <label>
          {t('gr30.vulns.labelSeverity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.vulns.all')}</option>)}
          </select>
        </label>
        <label>
          {t('gr30.vulns.labelStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr30.vulns.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr30.vulns.loading')}</p> : (
        <>
          <p aria-label="vuln-count">{t('gr30.vulns.count', { count: String(rows.length) })}</p>
          {rows.length === 0 ? <p aria-label="empty">{t('gr30.vulns.empty')}</p> : (
            <table aria-label="vuln-table">
              <thead><tr><th>{t('gr30.vulns.colCode')}</th><th>{t('gr30.vulns.colTitle')}</th><th>{t('gr30.vulns.colSeverity')}</th><th>{t('gr30.vulns.colStatus')}</th><th>{t('gr30.vulns.colSla')}</th></tr></thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.vulnerabilityId}>
                    <td>{v.vulnerabilityCode}</td><td>{v.title}</td><td>{v.severity}</td>
                    <td>{v.status}</td><td>{v.slaBreached ? t('gr30.vulns.breached') : t('gr30.vulns.onTrack')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
